import React from 'react';
import 'react-app-polyfill/stable';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Routes, Route, BrowserRouter } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.min.css';
import 'core-js/actual/array/find-last';
import 'core-js/actual/promise/finally';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { createBaseGainsightProvider } from '@rsos/capstone';
import '@rsos/capstone/src/overridePublicPath';
import ErrorBoundary from '@rsos/components/capstone/ErrorBoundary';
import NotFound from '@rsos/components/capstone/ErrorBoundary/NotFound';
import Layout from '@rsos/components/capstone/Layout';
import { OpenFeatureProvider } from '@rsos/feature-flags';
import { gspx } from '@rsos/gainsight';
import flattenArray from '@rsos/utils/flattenArray';
import { initPortalFlags } from '@rsos/utils/initFlags';
import { getAmplitudeApiKey, getProxyOrigin } from '@rsos/utils/metaTags';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import App from './app/App';
import { DebugPanelGate } from './debug/DebugPanelGate';
import { store, persistor } from './store';

const dataProvider = createBaseGainsightProvider(store);
// Initialize Gainsight with data provider and error handling
gspx.initialize(dataProvider, {
  captureError: captureExceptionWithScope,
  amplitude: {
    apiKey: getAmplitudeApiKey(),
    proxyOrigin: getProxyOrigin(),
  },
});

const topLevelRoutes = [
  'home',
  'query-interface',
  'irp-home',
  'irp-query-interface',
];

const appPaths = flattenArray(topLevelRoutes.map(route => route));

const AppRoutes = () => (
  <Routes>
    {topLevelRoutes.map(route => (
      <Route key={route} path={route} element={<App />} />
    ))}
    <Route path="*" element={<NotFound />} />
  </Routes>
);

const container = document.getElementById('root');

const root = createRoot(container);

// Register the Datadog OpenFeature provider so flags resolve via OpenFeature.
// No-ops (default provider) without creds. Mounted above the debug panel.
initPortalFlags();

root.render(
  <OpenFeatureProvider>
    <Provider store={store}>
      <DebugPanelGate>
        <PersistGate loading={'Loading'} persistor={persistor}>
          <BrowserRouter>
            <Layout appPaths={appPaths} persistor={persistor}>
              <ErrorBoundary>
                <AppRoutes />
              </ErrorBoundary>
            </Layout>
          </BrowserRouter>
        </PersistGate>
      </DebugPanelGate>
    </Provider>
  </OpenFeatureProvider>,
);
